import React, { ReactNode, useEffect, useState } from 'react';
import './SidePopover.less';
import Icon from '../Icon/Icon';
import classNames from 'classnames';
import BodyEnd from './BodyEnd';
import { EnvagoAria } from '../../../aria.types';

type Props = {
    children: ReactNode;
    text?: string | ReactNode;
    hidden?: boolean;
    contentClassName?: string;
    title?: string;
    aria?: EnvagoAria;
};

const SidePopover = ({ children, text, contentClassName, title, hidden = false, aria }: Props) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.code === 'Escape') {
                setVisible(false);
            }
        };
        window.addEventListener('keydown', handleEsc);

        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, []);

    if (hidden) return null;

    return (
        <div
            {...aria}
            aria-hidden={hidden}
            aria-owns={'sidepopover-content'}
            className={'transition-all'}
            style={{
                position: 'absolute',
                right: 0,
            }}
        >
            <button
                onClick={() => {
                    setVisible(!visible);
                }}
                aria-label={visible ? 'Hilfe schließen' : 'Hilfe öffnen'}
                role={'button'}
                style={{}}
                className={'cursor-pointer p-4 bg-accent rounded-l-lg text-white'}
            >
                <div className={'inline-flex flex-col text-white gap-1'}>{text}</div>
            </button>

            <BodyEnd>
                <SidePopoverContent
                    id={'sidepopover-content'}
                    aria={{ 'aria-hidden': !visible, role: 'dialog' }}
                    title={title}
                    className={contentClassName}
                    visible={visible}
                    setVisible={setVisible}
                >
                    {children}
                </SidePopoverContent>
            </BodyEnd>
        </div>
    );
};

const SidePopoverContent = ({
    visible,
    setVisible,
    children,
    className,
    title,
    id,
    aria,
}: {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    children: ReactNode;
    className?: string;
    title?: string;
    id?: string;
    aria?: EnvagoAria;
}) => {
    return (
        <div
            id={id}
            style={{ zIndex: 999, right: visible ? 0 : '-100%', minWidth: 320 }}
            {...aria}
            className={classNames(
                className,
                'fixed duration-500 transition-all md:max-w-md shadow-xl flex flex-col z-9 bg-white max-w-full top-0 bottom-0 right-0',
            )}
        >
            <div className={'flex w-full items-center mt-2'}>
                <h2 className={'ml-4'}>{title}</h2>
                <div className={'flex-1'} />
                <button
                    aria-hidden={true}
                    className={'self-end p-2 cursor-pointer'}
                    onClick={() => {
                        setVisible(!visible);
                    }}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') {
                            return;
                        }
                        setVisible(!visible);
                    }}
                >
                    <Icon aria={{ 'aria-hidden': true }} path={Icon.Path.mdiClose} className={'w-8 text-gray-600 hover:text-gray-500 transition'} />
                </button>
            </div>

            <div className={'flex-1 h-full max-h-full overflow-y-auto'}>
                <div className={''}>{children}</div>
            </div>
        </div>
    );
};

export default SidePopover;
